import { holdingStatusFromAnswer, ProductHoldingStatus } from '../registry/productReadiness';
import { MetricEnvelope, readerFor, tableFrom } from './metabaseTable';
import { readingRowsOnly } from './wtr';

/**
 * The Product Readiness column schema — the `*-PR-*` metrics, across the two generations the one
 * endpoint answers with:
 *
 *   - `C-*-PR-*`, 25 columns, one row per customer per product, captured on `api-uat.perxtech.io`
 *     2026-09-04 with every cell null.
 *   - `O-*-PR-*`, 22 columns, captured POPULATED on the same host 2026-09-09 for tenant industry
 *     `fintech_wallet_payment` — 434 rows on each of `O-FIN-PR-001`..`009`, 20,226 customers. It
 *     drops seven of the columns above (`customer_id`, `propensity_score`, `has_active_holding`,
 *     `eligibility_reason_codes`, `exclusion_reason`, `excluded_from_marketing`,
 *     `composite_confidence`) and adds the nine at the foot of this interface.
 *
 * A SEPARATE type, not a variant of `MetricResultRow`: this schema has no `metric_id`, no
 * `entity_type`, no `value_state`, no `calculation_status`, no `reason_code` and no `metric_value`.
 * Forcing it through the standard row would manufacture a `calculation_status: ''` for every one of
 * these metrics and then degrade them to `unavailable` on the strength of a column that was never
 * asked for. Contract PLA-8825, PLA-8828.
 *
 * The first 25 fields stay nullable and REQUIRED: they are the `C-*` contract and 27 shipped
 * assertions compare the row object whole. The nine `O-*` columns are optional and spread only where
 * the response declares them, so a `C-*` row is byte-identical to what it always was.
 */
export interface ProductReadinessRow {
  tenant_id: string | null;
  customer_id: string | null;
  product_offer_id: string | null;
  product_code: string | null;
  product_family: string | null;
  industry: string | null;
  catalog_lifecycle_status: string | null;
  readiness_model_status: string | null;
  readiness_not_configured: boolean | null;
  /** The with/without-holding branch the card's layouts already split on. */
  has_active_holding: boolean | null;
  eligibility_decision: string | null;
  /**
   * `type/Text` on the wire despite the plural name. Whether it carries one code, a delimited list
   * or a JSON array is UNVERIFIED — no populated cell has been seen — so it is kept as the string
   * the wire sent rather than split on a separator nobody has confirmed.
   */
  eligibility_reason_codes: string | null;
  eligibility_evaluated_at: string | null;
  /** `type/Decimal`. The catalogue's propensity scale is the server's to state, not this file's. */
  propensity_score: number | null;
  propensity_band: string | null;
  composite_confidence: number | null;
  /** Read from the wire's `handoff_confidence_ok_2` in preference; see the parse below. PLA-8828. */
  handoff_confidence_ok: boolean | null;
  propensity_scored_at: string | null;
  exclusion_reason: string | null;
  excluded_from_marketing: boolean | null;
  period_start: string | null;
  period_end: string | null;
  formula_display_text: string | null;
  governed_formula_expression: string | null;
  governed_formula_version: string | null;

  /* The `O-*-PR-*` generation's own columns, captured populated on `api-uat.perxtech.io` 2026-09-09
     for tenant industry `fintech_wallet_payment` (`O-FIN-PR-001`..`009`, 434 rows each). OPTIONAL
     rather than nullable: the `C-*` generation above declares none of them, and a key present with a
     `null` would change the row object every existing consumer compares against. PLA-8815. */

  /**
   * The grain. A row is NOT one customer: it is one cell of
   * `product_offer_id x readiness_branch x target_product_holding_status x eligibility_decision x
   * propensity_band`, carrying `n_customers`. The cells are disjoint — a `null` in either of the two
   * last dimensions is a CATEGORY ("no band scored", "no decision recorded") and not a subtotal,
   * which is what makes summing `n_customers` a population count rather than a double count.
   * Verified: the band-null cells do not equal the sum of the banded ones (27 against 8 on
   * `bill_pay` / `non_holder`), so they cannot be marginals.
   */
  n_customers?: number | null;
  /** `acquisition` / `existing_holder` — the same split `target_product_holding_status` states. */
  readiness_branch?: string | null;
  /** `holder` / `non_holder`. The catalogue's own holding field, now answered. */
  target_product_holding_status?: string | null;
  readiness_state?: string | null;
  /** Declared by the schema and `null` on all 434 captured rows, so no ring is drawn from it. */
  readiness_band?: string | null;
  confidence_score?: number | null;
  confidence_band?: string | null;
  confidence_limiting_reason?: string | null;
  confidence_cap_applied?: boolean | null;

  /* The governed readiness block, off the 64-column `C-*-PR-*` schema captured on
     `api-uat.perxtech.io` 2026-09-18 (`C-BFS-PR-001`, tenant `generic`). OPTIONAL for the reason the
     block above is: the 25-column generation declares none of them, and a key present with a `null`
     would change the row object 27 shipped assertions compare whole.

     Every one is schema-only today -- all nine `C-BFS-PR-*` answer 200 with ZERO rows for customer
     979417 -- so each reading built on them below degrades to what it did before when the column is
     absent or the cell null. `handoff_confidence_ok_2` is on the same response and is NOT declared
     as a field of its own: it is a duplicated join artifact on the data side, so the parse below
     folds it into `handoff_confidence_ok` and prefers it there. PLA-8828. */

  /**
   * The readiness figure itself, `type/Decimal` and on a 0-100 scale the catalogue bands at 75 and
   * 50. NOT `propensity_score`, which is 0-1 and a different quantity: a 0.62 propensity and a 62
   * readiness are not the same reading, which is why this is its own field rather than a second
   * source for that one.
   */
  readiness_score?: number | null;
  /**
   * The governed branch input, `type/BigInteger`: 0 is the acquisition / initial-adoption base and
   * `>= 1` the existing-holder base (catalogue `Calculation & Governance Rules!C51:G51`). It states
   * the same split `target_product_holding_status` words, and is preferred over it below because a
   * count is the catalogue's own formulation.
   */
  target_product_holding_count?: number | null;
  /** Why the reading is in the Insufficient Data state -- a governed code, keyed through i18n. */
  readiness_insufficiency_reason?: string | null;
  /** Why the reading is Suppressed -- a governed code, keyed the same way. */
  readiness_suppression_reason?: string | null;
  readiness_domain?: string | null;
}

/**
 * The two columns that identify this schema and appear in no other. `product_offer_id` alone would
 * be a weaker test than it looks — a future question could return it beside a `metric_value` — so
 * the readiness-specific status column is required with it.
 */
const READINESS_MARKERS = ['product_offer_id', 'readiness_model_status'];

export const isProductReadinessEnvelope = (envelope: MetricEnvelope): boolean => {
  const read = readerFor(tableFrom(envelope).cols);

  return READINESS_MARKERS.every(name => read.has(name));
};

export const productReadinessRowsFromEnvelope = (
  envelope: MetricEnvelope
): ProductReadinessRow[] => {
  const { cols, rows } = tableFrom(envelope);
  const read = readerFor(cols);

  // Reason rows carry none of the 25 readiness columns, so one left in would draw an empty product
  // row beside the real offers. Same partition the scalar and reversal parses make. PLA-8828.
  return readingRowsOnly(read, rows).map(row => ({
    tenant_id: read.text(row, 'tenant_id'),
    customer_id: read.text(row, 'customer_id'),
    product_offer_id: read.text(row, 'product_offer_id'),
    product_code: read.text(row, 'product_code'),
    product_family: read.text(row, 'product_family'),
    industry: read.text(row, 'industry'),
    catalog_lifecycle_status: read.text(row, 'catalog_lifecycle_status'),
    readiness_model_status: read.text(row, 'readiness_model_status'),
    readiness_not_configured: read.boolean(row, 'readiness_not_configured'),
    has_active_holding: read.boolean(row, 'has_active_holding'),
    eligibility_decision: read.text(row, 'eligibility_decision'),
    eligibility_reason_codes: read.text(row, 'eligibility_reason_codes'),
    eligibility_evaluated_at: read.text(row, 'eligibility_evaluated_at'),
    propensity_score: read.number(row, 'propensity_score'),
    propensity_band: read.text(row, 'propensity_band'),
    composite_confidence: read.number(row, 'composite_confidence'),
    // A duplicated-column defect on the data side. The first copy is null on 135 of 153 live rows
    // and contradicts the governed 0.60 handoff threshold on all 9 where the two differ, so the
    // second copy is preferred until the data side fixes the join. PLA-8828.
    handoff_confidence_ok:
      read.boolean(row, 'handoff_confidence_ok_2') ?? read.boolean(row, 'handoff_confidence_ok'),
    propensity_scored_at: read.text(row, 'propensity_scored_at'),
    exclusion_reason: read.text(row, 'exclusion_reason'),
    excluded_from_marketing: read.boolean(row, 'excluded_from_marketing'),
    period_start: read.text(row, 'period_start'),
    period_end: read.text(row, 'period_end'),
    formula_display_text: read.text(row, 'formula_display_text'),
    governed_formula_expression: read.text(row, 'governed_formula_expression'),
    governed_formula_version: read.text(row, 'governed_formula_version'),
    // Spread per column rather than read unconditionally, so a response on the older `C-*` schema
    // produces the identical object it always did. PLA-8815.
    ...(read.has('n_customers') ? { n_customers: read.number(row, 'n_customers') } : {}),
    ...(read.has('readiness_branch')
      ? { readiness_branch: read.text(row, 'readiness_branch') }
      : {}),
    ...(read.has('target_product_holding_status')
      ? { target_product_holding_status: read.text(row, 'target_product_holding_status') }
      : {}),
    ...(read.has('readiness_state') ? { readiness_state: read.text(row, 'readiness_state') } : {}),
    ...(read.has('readiness_band') ? { readiness_band: read.text(row, 'readiness_band') } : {}),
    ...(read.has('confidence_score')
      ? { confidence_score: read.number(row, 'confidence_score') }
      : {}),
    ...(read.has('confidence_band') ? { confidence_band: read.text(row, 'confidence_band') } : {}),
    ...(read.has('confidence_limiting_reason')
      ? { confidence_limiting_reason: read.text(row, 'confidence_limiting_reason') }
      : {}),
    ...(read.has('confidence_cap_applied')
      ? { confidence_cap_applied: read.boolean(row, 'confidence_cap_applied') }
      : {}),
    ...(read.has('readiness_score')
      ? { readiness_score: read.number(row, 'readiness_score') }
      : {}),
    ...(read.has('target_product_holding_count')
      ? { target_product_holding_count: read.number(row, 'target_product_holding_count') }
      : {}),
    ...(read.has('readiness_insufficiency_reason')
      ? { readiness_insufficiency_reason: read.text(row, 'readiness_insufficiency_reason') }
      : {}),
    ...(read.has('readiness_suppression_reason')
      ? { readiness_suppression_reason: read.text(row, 'readiness_suppression_reason') }
      : {}),
    ...(read.has('readiness_domain')
      ? { readiness_domain: read.text(row, 'readiness_domain') }
      : {})
  }));
};

/**
 * The holding branch the layouts read, from whichever of the three columns the response carries.
 *
 * `target_product_holding_count` first: it is the input the catalogue itself branches on -- `= 0` is
 * the acquisition base and `>= 1` the existing-holder base (`Calculation & Governance Rules!C51:G51`)
 * -- so where the wire sends the count, the count decides. It is on the 64-column `C-*` schema only;
 * the landed `O-*` generation does not carry it, which is why the other two stay.
 *
 * `target_product_holding_status` second: the column the `O-*-PR-*` generation actually answers with
 * (`holder` / `non_holder` on all 434 captured rows). `has_active_holding` third -- the 25-column
 * `C-*` generation's boolean, absent from `O-*`. Reading only the boolean is why this helper
 * answered `unknown` for every live row. PLA-8815, PLA-8828.
 *
 * A null holding stays `unknown`, NEVER `not_held` — `registry/productReadiness.ts` states the rule
 * and the reason: the catalogue's Not Applicable treatment turns on a CONFIRMED non-holder, so
 * reading a missing answer as "No" would suppress the limit, utilisation and repayment tiles for a
 * customer nobody has checked. PLA-8828.
 */
export const holdingStatusFromReadinessRow = (
  row: ProductReadinessRow | undefined
): ProductHoldingStatus => {
  if (!row) {
    return 'unknown';
  }

  // A finite count is an answer either way, `0` included -- which is the one case a truthiness test
  // would drop, and it is the acquisition branch, the larger of the two. PLA-8828.
  if (
    typeof row.target_product_holding_count === 'number' &&
    Number.isFinite(row.target_product_holding_count)
  ) {
    return row.target_product_holding_count >= 1 ? 'held' : 'not_held';
  }

  const stated = holdingStatusFromAnswer(row.target_product_holding_status);

  if (stated !== 'unknown') {
    return stated;
  }

  if (typeof row.has_active_holding !== 'boolean') {
    return 'unknown';
  }

  return row.has_active_holding ? 'held' : 'not_held';
};

export default productReadinessRowsFromEnvelope;
